import { useState } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { supabase } from "@/integrations/supabase/client";
import { track as gaTrack } from "@/lib/analytics";
import { track } from "@/lib/track";
import { useTenant } from "@/hooks/useTenant";
import { isUndefinedColumn } from "@/hooks/useProperties";

export const alertSignupSchema = z.object({
  email: z.string().email("Use a valid email"),
  preferred_areas: z.array(z.string()).optional().default([]),
  preferred_bedrooms: z.string().optional(),
  preferred_budget: z.string().optional(),
  preferred_amenities: z.array(z.string()).optional().default([]),
});

export type AlertSignupInput = z.infer<typeof alertSignupSchema>;

type Status = "idle" | "submitting" | "success";

export interface UseEmailCaptureArgs {
  source: string;
  defaults?: {
    area?: string;
    bedrooms?: number;
    budget?: string;
    /** Amenity labels (renter facets), e.g. the active browse filters. */
    amenities?: string[];
  };
  onSuccess?: (values: AlertSignupInput, meta: { captureId: string | null }) => void;
}

export function useEmailCapture({ source, defaults, onSuccess }: UseEmailCaptureArgs) {
  const tenant = useTenant();
  const [status, setStatus] = useState<Status>("idle");
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<AlertSignupInput>({
    resolver: zodResolver(alertSignupSchema),
    defaultValues: {
      email: "",
      preferred_areas: defaults?.area ? [defaults.area] : [],
      preferred_bedrooms: defaults?.bedrooms ? String(defaults.bedrooms) : "",
      preferred_budget: defaults?.budget ?? "",
      preferred_amenities: defaults?.amenities ?? [],
    },
  });

  const onSubmit: SubmitHandler<AlertSignupInput> = async (values) => {
    setServerError(null);
    setStatus("submitting");

    const areas = values.preferred_areas ?? [];
    gaTrack("alert_signup_submit_attempt", {
      source,
      has_area: areas.length > 0,
      has_bedrooms: !!values.preferred_bedrooms,
      has_budget: !!values.preferred_budget,
    });

    // Public alert signups should create a row once and silently ignore
    // repeat submissions for the same email. This avoids relying on anon
    // updates, which are intentionally restricted by RLS. Visitors can't read
    // email_captures either, so the id is made here and nothing is read back
    // (asking for the row back made RLS refuse every logged-out signup).
    const id = crypto.randomUUID();
    const row = {
      id,
      email: values.email,
      source,
      tenant_id: tenant.id,
      preferred_area: areas.length > 0 ? areas.join(",") : null,
      preferred_bedrooms: values.preferred_bedrooms
        ? Number(values.preferred_bedrooms)
        : null,
      preferred_budget: values.preferred_budget || null,
    };
    const amenities = values.preferred_amenities ?? [];
    let { error } = await supabase
      .from("email_captures")
      .insert({ ...row, preferred_amenities: amenities });
    // Until the email_captures amenities SQL is pasted the column does not
    // exist; keep the signup itself working rather than losing the lead.
    if (error && isUndefinedColumn(error)) {
      ({ error } = await supabase.from("email_captures").insert(row));
    }

    if (error) {
      if (error.code === "23505") {
        gaTrack("alert_signup_submit_duplicate", { source });
        track("alert_signup_submit", { source, result: "duplicate" });
        setStatus("success");
        onSuccess?.(values, { captureId: null });
        return;
      }

      gaTrack("alert_signup_submit_error", { source, error_code: error.code });
      setServerError("Something went wrong. Try again in a moment.");
      setStatus("idle");
      return;
    }

    track("alert_signup_submit", { result: "success",
      source,
      has_area: areas.length > 0,
      has_bedrooms: !!values.preferred_bedrooms,
      has_budget: !!values.preferred_budget,
    });
    setStatus("success");
    onSuccess?.(values, { captureId: id });
  };

  return {
    form,
    status,
    serverError,
    submit: form.handleSubmit(onSubmit),
  };
}
